import ky from 'ky'
import { env } from '@/env'
import type {
  CompiledSlotDTO,
  DeviceCommandAck,
  HeartbeatResponse,
  MarketplaceAuctionResponse,
  PairingStatusResponse,
  RequestPairingCodeResponse
} from '@/types/player.types'
import { detectPlayerType } from '@/utils/player-type'
import { api } from './api'
import { validateSchedulePayload } from './schedule-validator'

export interface HeartbeatPayload {
  firmwareVersion?: string
  ipAddress?: string
  playerRelease: string
  memoryUsedMb?: number
  storageCacheUsedMb?: number
  scheduleCacheAgeSeconds?: number
  pendingPlayLogsCount?: number
  playlogDeadLetterCount?: number
  currentSlotId?: string | null
  currentPlaylistId?: string | null
  currentAssetId?: string | null
  resolutionWidth?: number
  resolutionHeight?: number
  lastErrorCode?: string | null
  lastErrorAt?: string | null
}

// ─── Service ─────────────────────────────────────────────────────────────────

// Instância pública sem auth — usada antes de ter credenciais
const publicApi = ky.create({ prefixUrl: env.VITE_API_URL, timeout: 15_000 })

// O código de 6 caracteres fica visível na tela, então a API exige também o segredo devolvido
// no /request para entregar o deviceToken. Fica só em memória: se a TV reiniciar no meio do
// pareamento, a tela pede um código novo de qualquer forma.
const claimSecrets = new Map<string, string>()
const pairingHeaders = (code: string) => ({ 'x-pairing-secret': claimSecrets.get(code) ?? '' })

export const deviceService = {
  /**
   * Player solicita um código de pareamento ao servidor.
   * Retorna o código de 6 dígitos para exibir na tela.
   */
  requestPairingCode: async (hardwareId: string) => {
    const res = await publicApi
      .post('pairing/request', {
        json: { hardwareId, playerType: detectPlayerType() }
      })
      .json<RequestPairingCodeResponse>()
    claimSecrets.clear()
    claimSecrets.set(res.code, res.claimSecret)
    return res
  },

  /**
   * Player poleia o status do código a cada 3s.
   * Quando status = 'paired', retorna deviceId e (até o player confirmar via
   * acknowledgePairing) deviceToken.
   */
  getPairingStatus: (code: string) =>
    publicApi.get(`pairing/${code}/status`, { headers: pairingHeaders(code) }).json<PairingStatusResponse>(),

  /**
   * Confirma para o servidor que o deviceToken recebido em getPairingStatus já foi
   * persistido localmente — só então o servidor apaga o plaintext do banco. Sem isso,
   * uma única resposta perdida deixaria o token irrecuperável para sempre.
   */
  acknowledgePairing: (code: string) =>
    publicApi.post(`pairing/${code}/ack`, { headers: pairingHeaders(code) }).then(() => {
      claimSecrets.delete(code)
    }),

  heartbeat: (deviceId: string, payload: HeartbeatPayload) =>
    api.post(`devices/${deviceId}/heartbeat`, { json: payload }).json<HeartbeatResponse>(),

  /** Confirma um comando remoto entregue no heartbeat. */
  ackCommand: async (deviceId: string, commandId: string, ack: DeviceCommandAck): Promise<void> => {
    await api.post(`devices/${deviceId}/commands/${commandId}/ack`, { json: ack })
  },

  createMarketplaceAuction: (deviceId: string, listingId: string, guestCampaignId: string) =>
    api
      .post(`devices/${deviceId}/marketplace-auctions`, {
        json: { listingId, guestCampaignId }
      })
      .json<MarketplaceAuctionResponse>(),

  getSchedule: (deviceId: string) =>
    api
      .get(`devices/${deviceId}/schedule`, {
        headers: { 'x-player-release': env.VITE_PLAYER_RELEASE }
      })
      .json<CompiledSlotDTO[]>()
      .then(validateSchedulePayload)
}
